import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check } from "lucide-react";

const slug = (s) => s.replace(/\s+/g, "-").toLowerCase();

// Custom, fully theme-able dropdown (replaces native <select> whose option list
// renders white-on-white and is unreadable on the dark theme across devices).
export const Dropdown = ({ value, onChange, options, placeholder = "Select an option", testId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex w-full items-center justify-between rounded-xl border border-[var(--border-subtle)] bg-[rgba(255,245,230,0.03)] px-4 py-3 text-sm outline-none transition-colors hover:border-[var(--border-hover)] focus:border-[var(--border-hover)] focus-ring"
      >
        <span className={value ? "text-[var(--text-primary)]" : "text-[var(--text-muted)]"}>{value || placeholder}</span>
        <ChevronDown size={18} className={`text-[var(--text-secondary)] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            data-lenis-prevent
            className="no-scrollbar absolute left-0 right-0 z-40 mt-2 max-h-[min(320px,50vh)] overflow-y-auto overscroll-contain rounded-xl border border-[var(--border-hover)] bg-[rgba(20,18,16,0.98)] p-1.5 shadow-[var(--shadow-md)] backdrop-blur-xl"
          >
            {options.map((opt) => {
              const active = value === opt;
              return (
                <li key={opt}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    data-testid={`${testId}-option-${slug(opt)}`}
                    onClick={() => {
                      onChange(opt);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors hover:bg-[rgba(196,149,106,0.16)] ${
                      active ? "bg-[rgba(196,149,106,0.12)] text-[var(--accent-secondary)]" : "text-[var(--text-secondary)]"
                    }`}
                  >
                    {opt}
                    {active && <Check size={15} className="text-[var(--accent-secondary)]" />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};
